import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';

export const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

// Browser speech-to-text (Web Speech API). onText(transcript, isFinal) fires as words arrive.
export function useSpeech(onText, { continuous = false } = {}) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const rec = useRef(null);
  const cb = useRef(onText);
  cb.current = onText;
  useEffect(() => () => { if (rec.current) rec.current.abort(); }, []);
  const toggle = () => {
    if (listening) { rec.current.stop(); return; }
    const r = new SR();
    r.lang = navigator.language || 'en-US'; r.interimResults = true; r.continuous = continuous;
    r.onresult = (e) => { const res = Array.from(e.results); cb.current(res.map((x) => x[0].transcript).join(' ').trim(), res.every((x) => x.isFinal !== false)); };
    r.onerror = (e) => { setError(e.error === 'not-allowed' ? 'Microphone permission was denied — tap to answer instead.' : 'Could not hear that — try again.'); setListening(false); };
    r.onend = () => setListening(false);
    rec.current = r; setError(''); setListening(true); r.start();
  };
  return { supported: !!SR, listening, error, toggle };
}

export const MicButton = ({ listening, onClick, label, testId }) => (
  <button type="button" className={`fl-mic ${listening ? 'fl-mic-on' : ''}`} onClick={onClick} aria-pressed={listening} aria-label={listening ? 'Stop listening' : label} title={listening ? 'Stop listening' : label} data-testid={testId}>{listening ? <MicOff size={16} /> : <Mic size={16} />}</button>
);

export const Listening = ({ children = 'Listening — speak now', testId }) => <span className="fl-small fl-listening" data-testid={testId}><i /> {children}</span>;

// Deterministic keyword mapping for the "Did it work?" answers — no inference.
const NUM = { one: 1, two: 2, three: 3, four: 4, five: 5, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5 };
export function parseVoiceAnswers(text) {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ')} `;
  const has = (...ws) => ws.some((w) => t.includes(` ${w} `));
  const out = {};
  if (has('easier', 'easy')) out.went = 'EASIER THAN EXPECTED';
  else if (has('as expected', 'about expected', 'expected')) out.went = 'ABOUT AS EXPECTED';
  else if (has('difficult', 'hard', 'tough')) out.went = 'STILL DIFFICULT';
  if (has('a lot', 'lots', 'lot of help', 'much help')) out.help = 'A LOT';
  else if (has('a little', 'little', 'bit of help', 'some help')) out.help = 'A LITTLE';
  else if (has('no help', 'none', 'without help', 'on my own', 'myself')) out.help = 'NONE';
  if (has('did not', 'didn t', 'not yet', 'nope', 'have not')) out.performed = 'NO';
  else if (has('no') && !has('no help')) out.performed = 'NO';
  else if (has('yes', 'yeah', 'yep', 'i did', 'did it', 'done')) out.performed = 'YES';
  const m = t.match(/ (?:confidence|confident)(?: of| is| at| about| level)? (one|two|three|four|five|[1-5])(?: out of (?:five|5))? /) || t.match(/ (one|two|three|four|five|[1-5]) out of (?:five|5) /);
  if (m) out.confidence = NUM[m[1]];
  return out;
}

// Today screen: "ready" / "could use support" / "not confident" plus any barriers mentioned.
const BARRIER_WORDS = [['I need the procedure', ['procedure', 'manual', 'steps', 'instructions']], ["I haven't done this recently", ['recently', 'a while', 'long time', 'rusty', 'not done this']], ['I need the right equipment', ['equipment', 'tool', 'tools', 'gauge', 'parts']], ["I don't have enough time", ['time', 'rushed', 'deadline']], ['I need help', ['need help', 'need a hand', 'someone with me']]];
export function parseReadiness(text) {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9' ]/g, ' ').replace(/\s+/g, ' ')} `;
  const has = (...ws) => ws.some((w) => t.includes(` ${w} `) || t.includes(` ${w}`));
  let readiness = null;
  if (has('not confident', 'not ready', 'nervous', 'unsure', 'not sure', 'no confidence', 'worried')) readiness = 'NOT CONFIDENT YET';
  else if (has('support', 'could use', 'some help', 'bit of help', 'a hand', 'help')) readiness = 'COULD USE SUPPORT';
  else if (has('ready', 'good to go', 'all good', 'fine', 'got this', 'confident')) readiness = 'READY';
  const barriers = BARRIER_WORDS.filter(([, ws]) => has(...ws)).map(([b]) => b);
  return { readiness, barriers };
}

// Maya's observation: "approach four, consistency four, effectiveness five, minor prompt".
const SUPPORT_WORDS = [['Significant intervention', ['significant', 'intervention', 'stepped in', 'took over']], ['Some coaching', ['some coaching', 'coaching', 'coached']], ['Minor prompt', ['minor prompt', 'prompt', 'nudge', 'hint']], ['None', ['no support', 'none', 'no help', 'independent', 'on his own']]];
export function parseObservation(text) {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ')} `;
  const out = {};
  const num = (...labels) => { const m = t.match(new RegExp(` (?:${labels.join('|')})(?: was| is| of| at| a)? (one|two|three|four|five|[1-5]) `)); return m ? NUM[m[1]] : undefined; };
  const a = num('approach', 'method', 'sequence'), c = num('consistency', 'consistent', 'consistently'), e = num('effectiveness', 'effective', 'execution');
  if (a) out.approach = a; if (c) out.consistency = c; if (e) out.effectiveness = e;
  const sup = SUPPORT_WORDS.find(([, ws]) => ws.some((w) => t.includes(` ${w} `)));
  if (sup) out.support = sup[0];
  return out;
}
